// @ts-check
/**
 * src/lib/device-fingerprint.js
 *
 * WAR TEST 2026-05-21 — Device fingerprinting client-side ligero.
 *
 * MOTIVACIÓN:
 *   El `deviceId` de device-id.js es solo un UUID en localStorage. Un
 *   atacante que crea N cuentas desde el mismo browser solo necesita
 *   limpiar localStorage entre cuentas → nuevo UUID, mismas cuentas
 *   técnicamente "distintas".
 *
 *   El fingerprint complementa eso con señales NO borrables fácilmente:
 *     - Canvas rendering hash (depende de GPU + driver + OS)
 *     - WebGL vendor + renderer
 *     - User-agent + platform
 *     - Screen size + color depth
 *     - Timezone
 *     - Language
 *     - Hardware concurrency (CPU cores)
 *     - Touch support
 *     - DeviceMemory (cuando disponible)
 *
 *   Combinado → hash determinista que persiste a través de:
 *     - Borrar cookies
 *     - Limpiar localStorage
 *     - Cambiar de tab
 *     - Incógnito (mismo hash que regular)
 *
 *   Pero CAMBIA cuando:
 *     - Cambia de dispositivo
 *     - Cambia de browser
 *     - Update mayor del browser
 *
 *   Estabilidad esperada: ~95% mismo fingerprint del mismo device por
 *   meses. ~99% si user no cambia de browser version.
 *
 * NO ES PII — es un hash anónimo. No identifica a la PERSONA, solo
 * agrupa requests del mismo browser/device. Cumple GDPR cuando se usa
 * para security (legitimate interest) y NO para tracking publicitario.
 *
 * USO:
 *   const fp = await getDeviceFingerprint();
 *   // fp = "8a3f...c2d1" (16 chars hex, deterministico)
 *
 * INTEGRACIÓN: en signup body junto con deviceId.
 *   Backend agrupa: fingerprint → [deviceId1, deviceId2, ...]
 *   Si mismo fingerprint tiene 5+ deviceIds distintos en 7 días →
 *   audit ALERT (probable atacante con localStorage clean entre signups).
 */

/** @type {string|null} */
let _cachedFingerprint = null;

/** @param {string} text @returns {Promise<string>} */
async function _sha256(text) {
  if (typeof globalThis.crypto?.subtle?.digest === 'function') {
    const encoded = new TextEncoder().encode(text);
    const buf = await globalThis.crypto.subtle.digest('SHA-256', encoded);
    return Array.from(new Uint8Array(buf))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }
  // Fallback simple (no debería pasar en browsers modernos)
  let hash = 5381;
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) + hash + text.charCodeAt(i)) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

function _safeCanvasHash() {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'no-canvas';
    // Texto + forma que dependen de GPU + fontes para render diferenciado
    ctx.textBaseline = 'top';
    ctx.font = "14px 'Arial', sans-serif";
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Dr. Víctor 🛡️ FP', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Dr. Víctor 🛡️ FP', 4, 17);
    // Reading dataURL exposes GPU/driver differences
    return canvas.toDataURL().slice(-200);
  } catch {
    return 'canvas-err';
  }
}

function _safeWebGLInfo() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || /** @type {WebGLRenderingContext|null} */ (canvas.getContext(/** @type {any} */ ('experimental-webgl')));
    if (!gl) return 'no-webgl';
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    if (!dbg) return 'no-debug-ext';
    return [
      gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) || '',
      gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || '',
    ].join('|');
  } catch {
    return 'webgl-err';
  }
}

/**
 * Calcula el fingerprint determinista del browser actual.
 * Retorna string hex (primeros 16 chars del SHA-256) o null si runs server-side.
 *
 * Cacheado en memoria por session — múltiples llamadas mismo result.
 */
export async function getDeviceFingerprint() {
  if (typeof window === 'undefined') return null;
  if (_cachedFingerprint) return _cachedFingerprint;

  try {
    const signals = [
      navigator.userAgent || '',
      navigator.platform || '',
      navigator.language || '',
      (navigator.languages || []).join(','),
      String(navigator.hardwareConcurrency || 0),
      String((/** @type {any} */ (navigator)).deviceMemory || 0),  // Solo Chrome/Edge
      String(navigator.maxTouchPoints || 0),
      `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
      String(new Date().getTimezoneOffset()),
      Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      _safeCanvasHash(),
      _safeWebGLInfo(),
    ].join('|');

    const fullHash = await _sha256(signals);
    _cachedFingerprint = fullHash.slice(0, 16); // 16 chars hex = ~64 bits, suficiente
    return _cachedFingerprint;
  } catch (e) {
    // Si algo falla, retornamos un fingerprint degradado (UA + screen)
    // mejor que nada.
    try {
      const fallback = await _sha256(`${navigator.userAgent || ''}|${window.screen.width}x${window.screen.height}`);
      return fallback.slice(0, 16);
    } catch {
      return null;
    }
  }
}
